import React, { useState } from 'react';
import { Button } from '../ui';
import { VIEWPORT_TYPES, SCALE_MAP, createViewport } from '../../constants/paper';

/**
 * ViewportEditor - Panel for adding and managing viewports on a sheet
 */
const ViewportEditor = ({
  viewports = [],
  selectedViewportId,
  onAddViewport,
  onUpdateViewport,
  onDeleteViewport,
  onSelectViewport,
  onClose,
  isMobile = false,
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);

  const handleAddViewport = (type) => {
    const id = `viewport-${Date.now()}`;
    const newViewport = createViewport(id, type, 50, 50, 400, 300);
    onAddViewport(newViewport);
    setShowAddMenu(false);
  };

  const selectedViewport = viewports.find(v => v.id === selectedViewportId);

  const scaleOptions = Object.keys(SCALE_MAP);

  return (
    <div
      style={{
        position: 'absolute',
        top: isMobile ? '60px' : '60px',
        right: isMobile ? '10px' : '16px',
        background: 'rgba(8,12,16,0.98)',
        border: '1px solid rgba(0,200,255,0.2)',
        borderRadius: '10px',
        padding: '14px',
        width: isMobile ? 'calc(100% - 20px)' : '280px',
        maxWidth: '320px',
        maxHeight: isMobile ? '60vh' : '70vh',
        overflowY: 'auto',
        zIndex: 600,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>
          Viewports
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#6080a0',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          x
        </button>
      </div>

      {/* Add Viewport Button */}
      <div style={{ marginBottom: '12px', position: 'relative' }}>
        <Button
          variant="primary"
          onClick={() => setShowAddMenu(!showAddMenu)}
          style={{ width: '100%' }}
        >
          + Add Viewport
        </Button>

        {/* Add Viewport Menu */}
        {showAddMenu && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              background: '#1a2030',
              border: '1px solid rgba(0,200,255,0.3)',
              borderRadius: '6px',
              zIndex: 610,
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            }}
          >
            {Object.entries(VIEWPORT_TYPES).map(([type, info]) => (
              <div
                key={type}
                onClick={() => handleAddViewport(type)}
                style={{
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: '11px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(0,200,255,0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <span>{info.icon}</span>
                <span>{info.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Viewport List */}
      {viewports.length === 0 ? (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#6080a0',
          fontSize: '11px',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '6px',
        }}>
          No viewports yet. Add a viewport to display floor plans or elevations on this sheet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {viewports.map((viewport) => (
            <div
              key={viewport.id}
              onClick={() => onSelectViewport(viewport.id)}
              style={{
                padding: '10px',
                background: selectedViewportId === viewport.id ? 'rgba(0,200,255,0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selectedViewportId === viewport.id ? 'rgba(0,200,255,0.3)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{VIEWPORT_TYPES[viewport.type]?.icon || '📐'}</span>
                  <span style={{ color: '#fff', fontSize: '11px' }}>{viewport.label}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteViewport(viewport.id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff6666',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '2px 6px',
                  }}
                >
                  x
                </button>
              </div>
              <div style={{ marginTop: '4px', fontSize: '9px', color: '#6080a0' }}>
                {viewport.scale} | {Math.round(viewport.width)} x {Math.round(viewport.height)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Viewport Properties */}
      {selectedViewport && (
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: '12px',
        }}>
          <div style={{ color: '#6080a0', fontSize: '10px', textTransform: 'uppercase', marginBottom: '8px' }}>
            Viewport Properties
          </div>

          {/* Label */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
              Label
            </label>
            <input
              type="text"
              value={selectedViewport.label}
              onChange={(e) => onUpdateViewport(selectedViewport.id, { label: e.target.value })}
              style={{
                width: '100%',
                padding: '6px 8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '11px',
              }}
            />
          </div>

          {/* Scale */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
              Scale
            </label>
            <select
              value={selectedViewport.scale}
              onChange={(e) => onUpdateViewport(selectedViewport.id, { scale: e.target.value })}
              style={{
                width: '100%',
                padding: '6px 8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '11px',
              }}
            >
              {scaleOptions.map(scale => (
                <option key={scale} value={scale} style={{ background: '#1a2030' }}>
                  {scale}
                </option>
              ))}
            </select>
          </div>

          {/* Size */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Width
              </label>
              <input
                type="number"
                value={Math.round(selectedViewport.width)}
                onChange={(e) => onUpdateViewport(selectedViewport.id, { width: parseInt(e.target.value) || 100 })}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '11px',
                }}
              />
            </div>
            <div>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Height
              </label>
              <input
                type="number"
                value={Math.round(selectedViewport.height)}
                onChange={(e) => onUpdateViewport(selectedViewport.id, { height: parseInt(e.target.value) || 100 })}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '11px',
                }}
              />
            </div>
          </div>

          {/* Show Label Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="showLabel"
              checked={selectedViewport.showLabel}
              onChange={(e) => onUpdateViewport(selectedViewport.id, { showLabel: e.target.checked })}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="showLabel" style={{ color: '#8899aa', fontSize: '10px', cursor: 'pointer' }}>
              Show viewport label on sheet
            </label>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{
        marginTop: '12px',
        padding: '10px',
        background: 'rgba(0,200,255,0.05)',
        borderRadius: '6px',
        fontSize: '10px',
        color: '#6080a0',
      }}>
        Tip: Drag viewports on the sheet to reposition them. Drag corners to resize.
      </div>
    </div>
  );
};

export default ViewportEditor;
